import { Controller } from '@hotwired/stimulus'
import consumer from '../channels/consumer'

const BIKE_PERCENT = {
  offered: 22,
  assigned: 48,
  picked_up: 72,
  delivered: 100,
  canceled: 0
}

const DELIVERY_DONE_AT = {
  rider_accepted: ['assigned', 'picked_up', 'delivered'],
  picked_up: ['picked_up', 'delivered'],
  delivered: ['delivered']
}

export default class extends Controller {
  static targets = [
    'bike',
    'track',
    'midDot',
    'endDot',
    'assignedBadge',
    'assignedLabel',
    'deliveryBadge',
    'deliveryStep',
    'pinPanel',
    'pinButton',
    'pinValue',
    'pinError'
  ]

  static values = {
    orderId: Number,
    status: String,
    percent: { type: Number, default: 0 },
    pinUrl: String
  }

  connect () {
    this.applyPercent(this.percentValue, this.statusValue)
    this.updatePinPanel(this.statusValue)
    if (!this.orderIdValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: 'OrderTrackingChannel', order_id: this.orderIdValue },
      {
        received: (data) => this.handleMessage(data)
      }
    )
  }

  disconnect () {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleMessage (data) {
    if (!data?.assignment_status) return
    if (data.type !== 'assignment_status' && data.type !== 'rider_location') return

    const status = String(data.assignment_status)
    const percent = Number.isFinite(Number(data.bike_progress_percent))
      ? Number(data.bike_progress_percent)
      : BIKE_PERCENT[status]
    if (percent == null) return

    this.applyPercent(percent, status)
    this.updateAssignedUi(data, status)
    this.updateDeliverySteps(status)
    this.updatePinPanel(status)
  }

  updatePinPanel (status) {
    if (!this.hasPinPanelTarget) return

    const available = status === 'picked_up'
    this.pinPanelTarget.classList.toggle('hidden', !available)
    if (!available && this.hasPinValueTarget) {
      const digits = this.pinValueTarget.querySelector('[data-pin-digits]')
      if (digits) digits.textContent = ''
      this.pinValueTarget.classList.add('hidden')
      if (this.hasPinButtonTarget) {
        this.pinButtonTarget.classList.remove('hidden')
        this.pinButtonTarget.disabled = false
        this.pinButtonTarget.textContent = 'Rider aa gaya - PIN reveal karein'
      }
    }
  }

  async revealPin (event) {
    event.preventDefault()
    if (!this.hasPinUrlValue || !this.pinUrlValue) return

    const confirmed = window.confirm(
      'Kya rider aapke doorstep par pohanch gaya hai? PIN call ya chat par pehle share na karein.'
    )
    if (!confirmed) return

    if (this.hasPinButtonTarget) {
      this.pinButtonTarget.disabled = true
      this.pinButtonTarget.textContent = 'PIN loading…'
    }
    if (this.hasPinErrorTarget) this.pinErrorTarget.classList.add('hidden')

    try {
      const response = await fetch(this.pinUrlValue, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        cache: 'no-store'
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'PIN could not be loaded.')

      const digits = this.pinValueTarget.querySelector('[data-pin-digits]')
      if (digits) digits.textContent = data.pin
      this.pinValueTarget.classList.remove('hidden')
      this.pinButtonTarget.classList.add('hidden')
    } catch (error) {
      if (this.hasPinErrorTarget) {
        this.pinErrorTarget.textContent = error.message
        this.pinErrorTarget.classList.remove('hidden')
      }
      if (this.hasPinButtonTarget) {
        this.pinButtonTarget.disabled = false
        this.pinButtonTarget.textContent = 'Rider aa gaya - PIN reveal karein'
      }
    }
  }

  applyPercent (percent, status) {
    const value = Math.max(0, Math.min(100, Number(percent) || 0))
    const moving = status === 'assigned' || status === 'picked_up'

    if (this.hasTrackTarget) {
      this.trackTarget.style.width = `${Math.max(value, 8)}%`
      this.trackTarget.classList.toggle('is-live', moving)
      this.trackTarget.classList.toggle('bg-gradient-to-r', !moving)
      this.trackTarget.classList.toggle('from-sky-400', !moving)
      this.trackTarget.classList.toggle('to-emerald-400', !moving)
    }

    if (this.hasBikeTarget) {
      this.bikeTarget.classList.toggle('hidden', value <= 0)
      this.bikeTarget.style.setProperty('--bike-left', `${value}%`)
      this.bikeTarget.classList.toggle('is-moving', moving)
    }

    if (this.hasMidDotTarget) {
      this.midDotTarget.classList.toggle('bg-sky-500', value >= 48)
      this.midDotTarget.classList.toggle('bg-slate-300', value < 48)
    }

    if (this.hasEndDotTarget) {
      this.endDotTarget.classList.toggle('bg-emerald-500', value >= 100)
      this.endDotTarget.classList.toggle('bg-slate-300', value < 100)
    }
  }

  updateAssignedUi (data, status) {
    if (this.hasAssignedBadgeTarget) {
      this.assignedBadgeTarget.textContent = data.rider_name || 'Assigned'
      this.assignedBadgeTarget.classList.remove('hidden', 'text-slate-400')
      this.assignedBadgeTarget.classList.add('rounded-md', 'bg-sky-100', 'px-2', 'py-0.5', 'text-sky-800')
    }

    if (this.hasAssignedLabelTarget) {
      const labels = {
        offered: 'Rider assigned',
        assigned: 'Rider heading to shop',
        picked_up: 'On the way to you',
        delivered: 'Delivered'
      }
      this.assignedLabelTarget.textContent = labels[status] || 'Rider assigned'
    }
  }

  updateDeliverySteps (status) {
    if (!this.hasDeliveryStepTarget) return

    this.deliveryStepTargets.forEach((el) => {
      const key = el.dataset.stepKey
      const doneStatuses = DELIVERY_DONE_AT[key] || []
      const done = doneStatuses.includes(status)
      const dot = el.querySelector('[data-step-dot]')
      const meta = el.querySelector('[data-step-meta]')
      const connector = el.querySelector('[data-step-connector]')

      if (dot) {
        if (done) {
          dot.className = 'relative z-10 mx-auto flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-extrabold ring-2 bg-emerald-500 text-white ring-emerald-200'
          dot.innerHTML = '<svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>'
        } else {
          const index = el.dataset.stepIndex || '1'
          const active = key === 'rider_accepted' && ['offered', 'assigned'].includes(status)
          dot.className = active
            ? 'relative z-10 mx-auto flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-extrabold ring-2 bg-sky-100 text-sky-700 ring-sky-200'
            : 'relative z-10 mx-auto flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-extrabold ring-2 bg-white text-slate-400 ring-slate-200'
          dot.textContent = index
        }
      }

      if (meta && done && !meta.dataset.locked) {
        meta.textContent = 'Just now'
        meta.className = 'mt-0.5 text-[9px] tabular-nums text-slate-500'
      }

      if (connector) {
        const nextKey = connector.dataset.nextKey
        const nextDone = (DELIVERY_DONE_AT[nextKey] || []).includes(status)
        connector.classList.toggle('bg-emerald-300', done && nextDone)
        connector.classList.toggle('bg-slate-200', !(done && nextDone))
      }
    })

    if (this.hasDeliveryBadgeTarget) {
      if (status === 'delivered') {
        this.deliveryBadgeTarget.textContent = 'Delivered'
        this.deliveryBadgeTarget.className = 'text-[10px] font-bold uppercase tracking-wide text-emerald-600'
      } else {
        this.deliveryBadgeTarget.textContent = 'Delivery'
        this.deliveryBadgeTarget.className = 'text-[10px] font-bold uppercase tracking-wide text-slate-400'
      }
    }
  }
}
